/**
 * The typed client for the API's ANONYMOUS routes (M2-07: the customer's
 * estimate link) — the same generated types as `apiClient`, and none of its
 * middleware. Deliberately a second client rather than `apiClient` or a bare
 * `fetch`:
 *
 * - **No bearer token.** `apiClient` asks MSAL for one on every request. The
 *   customer has none, but the office has — and an office user opening the
 *   customer's link to check it would send their token to a route that must
 *   answer the same for everybody, and could be bounced to Entra by
 *   `acquireTokenRedirect` just for looking at it.
 * - **No sign-in redirect.** `apiClient`'s middleware reports every 401 to
 *   `MaintenanceGate`, which clears MSAL and sends the tab to `/signin`. A
 *   customer must never land on the office's sign-in page, whatever a proxy in
 *   front of the API answers.
 * - **Still typed.** A bare `fetch` would re-declare the response shape by
 *   hand; this reads it from the generated schema, so a change on the server
 *   breaks the build rather than the page.
 *
 * What it keeps from `apiClient` is the outage detection: a gateway status or
 * a network failure still goes to `reportApiUnavailable`, which confirms via
 * `/health` before showing the maintenance page.
 */
import createClient from 'openapi-fetch';
import type { paths } from './generated/schema';
import { reportApiUnavailable } from '@/lib/maintenance';

// Same base as `apiClient` — see its note on why not `?? ''`.
const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin;

export const publicClient = createClient<paths>({
  baseUrl,
  // Re-read on every call, so a test's `vi.stubGlobal('fetch', …)` reaches it
  // (the same indirection as `apiClient`).
  fetch: (input) => globalThis.fetch(input),
});

const GATEWAY_STATUSES = new Set([502, 504]);

publicClient.use({
  onResponse({ response }) {
    if (GATEWAY_STATUSES.has(response.status)) {
      reportApiUnavailable();
    }
    return response;
  },
  onError() {
    reportApiUnavailable();
    return undefined;
  },
});
